import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  showErrorToast,
  ColoredAlert,
} from '@bpinternal/ui-kit-next'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDownIcon,
  ChevronRightIcon,
  Loader2,
  Plus,
  SquareCheckIcon,
  SquareIcon,
  SquareMinusIcon,
  X,
} from 'lucide-react'
import { ComponentProps, useEffect, useRef, useState } from 'react'
import CheckboxTree, { type Node as RCNode } from 'react-checkbox-tree'
import { getUseBotOnboardingStore } from '~/features/bots/stores/useBotOnboardingStore'
import { useStepNavStore } from '~/features/bots/stores/useStepNavStore'
import { buildTree } from '~/features/bots/utils/buildTree'
import { safeParseURLS } from '~/features/bots/utils/urls'
import { getApiClient, useBotCreationStore } from '~/stores'
import { cn } from '~/utils'

type Props = {
  workspaceId: string
  botId: string
}

const MAX_URLS_PER_REQUEST = 1000
const MAX_CHECKED_URLS = 20

export const UrlsTabContent = ({ workspaceId, botId }: Props) => {
  const [includePattern, setIncludePattern] = useState('')
  const [excludePattern, setExcludePattern] = useState('')
  const [includeGlobs, setIncludeGlobs] = useState<string[]>([])
  const [excludeGlobs, setExcludeGlobs] = useState<string[]>([])
  const [isValidUrl, setIsValidUrl] = useState(false)
  const [urlError, setUrlError] = useState('')
  const [urlLimitWarning, setUrlLimitWarning] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false)
  const setStepNavDisabled = useStepNavStore((state) => state.setStepNavDisabled)
  const botsBeingCreated = useBotCreationStore((state) => state.botsBeingCreated)

  const useBotOnboardingStore = getUseBotOnboardingStore({ workspaceId, botId })
  const setWebsiteUrlsTree = useBotOnboardingStore((state) => state.setWebsiteUrlsTree)
  const websiteUrlsTree = useBotOnboardingStore((state) => state.websiteUrlsTree)
  const inputUrl = useBotOnboardingStore((state) => state.knowledgeBase.inputUrl)
  const setKnowledgeBaseInputUrl = useBotOnboardingStore((state) => state.setKnowledgeBaseInputUrl)

  const inputRef = useRef<HTMLInputElement>(null)

  const [nodes, setNodes] = useState<ComponentProps<typeof CheckboxTree>['nodes']>(
    websiteUrlsTree.urls.length > 0 ? buildTree(websiteUrlsTree.urls) : []
  )

  useEffect(() => {
    inputRef.current?.focus()
    validateUrl(inputUrl)
  }, [])

  const validateUrl = (url: string) => {
    if (url.length === 0) {
      setIsValidUrl(true)
      setUrlError('')
      return
    }
    try {
      const urlToValidate = url.includes('://') ? url : `https://${url}`
      new URL(urlToValidate)
      setIsValidUrl(true)
      setUrlError('')
    } catch {
      setIsValidUrl(false)
      setUrlError('Please enter a valid URL')
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value
    setKnowledgeBaseInputUrl(url)
    validateUrl(url)
  }

  const addIncludeGlob = () => {
    if (includePattern.trim() && !includeGlobs.includes(includePattern.trim())) {
      setIncludeGlobs([...includeGlobs, ...includePattern.split(',').map((glob) => glob.trim())])
      setIncludePattern('')
    }
  }

  const addExcludeGlob = () => {
    if (excludePattern.trim() && !excludeGlobs.includes(excludePattern.trim())) {
      setExcludeGlobs([...excludeGlobs, ...excludePattern.split(',').map((glob) => glob.trim())])
      setExcludePattern('')
    }
  }

  const removeIncludeGlob = (glob: string) => {
    setIncludeGlobs(includeGlobs.filter((g) => g !== glob))
  }

  const removeExcludeGlob = (glob: string) => {
    setExcludeGlobs(excludeGlobs.filter((g) => g !== glob))
  }

  const handleAddWebsite = async () => {
    if (!inputUrl.trim() || !isValidUrl) return

    setIsLoading(true)
    setStepNavDisabled(true)
    setUrlLimitWarning('') // Clear previous warnings

    try {
      const client = getApiClient({ workspaceId, botId })
      const urlToUse = inputUrl.includes('://') ? inputUrl : `https://${inputUrl}`

      const res = await client.callAction({
        type: 'browser:discoverUrls',
        input: {
          url: urlToUse,
          onlyHttps: true,
          count: MAX_URLS_PER_REQUEST,
          include: includeGlobs,
          exclude: excludeGlobs,
        },
      })

      const allUrls = safeParseURLS(res.output?.urls ?? [])
      const totalDiscovered = allUrls.length

      // Don't pre-check any URLs if more than 100 are discovered
      const checkedUrls = totalDiscovered > MAX_CHECKED_URLS ? [] : allUrls

      // Set warning if more URLs were discovered
      if (totalDiscovered > MAX_CHECKED_URLS) {
        setUrlLimitWarning(`Select up to ${MAX_CHECKED_URLS} URLs. You will be able to add more later.`)
      }

      const tree = buildTree(allUrls)
      setNodes(tree)
      setWebsiteUrlsTree({
        urls: allUrls,
        checked: checkedUrls,
        expanded: checkedUrls,
      })
    } catch (error) {
      showErrorToast(`Failed to discover URL`)
    } finally {
      setIsLoading(false)
      setStepNavDisabled(false)
    }
  }

  const handleCheckChange = (checked: string[], targetNode: RCNode) => {
    // Allow unchecking (when checked.length decreases)
    const currentChecked = websiteUrlsTree?.checked || []
    const isUnchecking = checked.length < currentChecked.length

    if (isUnchecking || checked.length <= MAX_CHECKED_URLS) {
      setUrlLimitWarning('')
      setWebsiteUrlsTree({ ...websiteUrlsTree, checked })
      return
    }

    const urlsFromNode: string[] = []
    let urlsCount = 0

    const collectUrls = (nodeList: RCNode[]) => {
      for (const node of nodeList) {
        if (urlsCount >= MAX_CHECKED_URLS - currentChecked.length) {
          return
        }
        if (node.children) {
          collectUrls(node.children)
        } else {
          if (!currentChecked.includes(node.value)) {
            urlsFromNode.push(node.value)
            urlsCount++
          }
        }
      }
    }

    collectUrls([targetNode])

    const newChecked = [...currentChecked, ...urlsFromNode]
    let newExpanded = websiteUrlsTree?.expanded

    if (newChecked.length >= MAX_CHECKED_URLS) {
      setUrlLimitWarning(`Select up to ${MAX_CHECKED_URLS} URLs. You will be able to add more later.`)

      // Expand the parent node so users can see which URLs were selected
      if (targetNode.children && !websiteUrlsTree?.expanded?.includes(targetNode.value)) {
        newExpanded = [...(websiteUrlsTree?.expanded || []), targetNode.value]
      }
    }

    setWebsiteUrlsTree({ ...websiteUrlsTree, checked: newChecked, expanded: newExpanded })
  }

  return (
    <>
      {/* URL Input Section */}
      <div className="flex flex-col gap-6">
        <div className="space-y-2">
          <Label htmlFor="url" className="text-sm ">
            Website URL
          </Label>
          <div className="relative px-px">
            <Input
              ref={inputRef}
              id="url"
              type="url"
              value={inputUrl}
              onChange={handleUrlChange}
              placeholder="https://yourwebsite.com"
              className={cn(
                'pr-10 text-sm',
                urlError && 'border-destructive focus-visible:ring-destructive',
                isValidUrl && inputUrl && 'border-primary-700 focus-visible:ring-primary-700'
              )}
            />
            {isValidUrl && inputUrl && <CheckCircle className="absolute right-3 top-2.5 h-4 w-4 text-primary-700" />}
            {urlError && <AlertCircle className="absolute right-3 top-2.5 h-4 w-4 text-destructive" />}
          </div>
          {urlError && <p className="text-xs text-destructive sm:text-sm">{urlError}</p>}
        </div>

        <Collapsible open={advancedSettingsOpen} onOpenChange={setAdvancedSettingsOpen} className="group">
          <CollapsibleTrigger className="flex gap-2">
            <Label>Advanced settings</Label>
            <ChevronRightIcon
              size={16}
              className="transition-transform duration-200 group-data-[state=open]:rotate-90"
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4 flex flex-col gap-2">
            {/* Include Patterns */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="include" className="text-sm">
                Include Patterns
              </Label>
              <div className="flex space-x-2">
                <Input
                  id="include"
                  value={includePattern}
                  onChange={(e) => setIncludePattern(e.target.value)}
                  placeholder="docs, /blog/"
                  className="h-10"
                  onKeyPress={(e) => e.key === 'Enter' && addIncludeGlob()}
                  onBlur={() => {
                    if (includePattern.trim()) {
                      addIncludeGlob()
                    }
                  }}
                />
                <Button onClick={addIncludeGlob} disabled={!includePattern.trim()} size="icon" variant="ghost">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {includeGlobs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {includeGlobs.map((glob, index) => (
                    <Badge key={index} variant="secondary" className="bg-green-200 text-green-600 hover:bg-green-300">
                      {glob}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-1 h-auto p-0 text-green-600 hover:bg-transparent hover:text-green-700"
                        onClick={() => removeIncludeGlob(glob)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Exclude Patterns */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="exclude">Exclude Patterns</Label>
              <div className="flex space-x-2">
                <Input
                  id="exclude"
                  value={excludePattern}
                  onChange={(e) => setExcludePattern(e.target.value)}
                  placeholder="admin, /api/"
                  onKeyUp={(e) => e.key === 'Enter' && addExcludeGlob()}
                  className="h-10"
                  onBlur={() => {
                    if (excludePattern.trim()) {
                      addExcludeGlob()
                    }
                  }}
                />
                <Button onClick={addExcludeGlob} disabled={!excludePattern.trim()} size="icon" variant="ghost">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {excludeGlobs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {excludeGlobs.map((glob, index) => (
                    <Badge key={index} variant="destructive" className="bg-red-200 text-red-600 hover:bg-red-300">
                      {glob}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-1 h-auto p-0 text-red-600 hover:bg-transparent hover:text-red-700"
                        onClick={() => removeExcludeGlob(glob)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Button
          variant="secondary"
          onClick={handleAddWebsite}
          disabled={
            !inputUrl || !isValidUrl || isLoading || (botsBeingCreated[botId] && botsBeingCreated[botId] !== 'deployed')
          }
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Discovering URLs...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-5 w-5" />
              Discover URLS
            </>
          )}
        </Button>
      </div>

      {/* URL Limit Warning */}
      {urlLimitWarning && (
        <ColoredAlert color="amber" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-sm">{urlLimitWarning}</AlertDescription>
        </ColoredAlert>
      )}

      <CheckboxTree
        icons={{
          check: <SquareCheckIcon color="var(--icon-color)" />,
          uncheck: <SquareIcon color="var(--icon-color)" />,
          halfCheck: <SquareMinusIcon color="var(--icon-color)" />,
          expandClose: <ChevronRightIcon color="var(--icon-color)" />,
          expandOpen: <ChevronDownIcon color="var(--icon-color)" />,
          expandAll: null,
          collapseAll: null,
          parentClose: null,
          parentOpen: null,
          leaf: null,
        }}
        {...websiteUrlsTree}
        nodes={nodes ?? []}
        onExpand={(expanded) => setWebsiteUrlsTree({ ...websiteUrlsTree, expanded })}
        onCheck={handleCheckChange}
        optimisticToggle={false}
      />
    </>
  )
}
